// @ts-nocheck
import React from "react";
import { useAuth } from "../context/AuthContext";
import { Link, Navigate } from "react-router-dom";

const roleWidgets = {
  superadmin: [
    {
      label: "Open Superadmin Panel",
      to: "/superadmin/home",
      desc: "Manage schools, provision admins/teachers/students, and oversee multi-school setup.",
      action: true,
    },
  ],
  admin: [
    { label: "Approve Features", to: "/admin/approve-features", desc: "Approve new features, teachers, and students.", action: true },
    { label: "Manage Teachers", to: "/admin/teachers", desc: "View and approve teachers.", action: true },
    { label: "Manage Students", to: "/admin/students", desc: "View and approve students.", action: true },
    { label: "School Analytics", to: "/grades", desc: "View school-wide grading and standards alignment.", action: false },
    { label: "Feedback Overview", to: "/feedback", desc: "Monitor communication and engagement.", action: false },
  ],
  teacher: [
    { label: "Rate Student Performance", to: "/teacher/rate-performance", desc: "Scale/rate students by feature, subject, and class.", action: true },
    { label: "Manage Students", to: "/teacher/students", desc: "View, filter, and group your students by progress, class, or section.", action: true },
    { label: "Student Feedback", to: "/feedback", desc: "Send actionable feedback to students and families.", action: false },
  ],
};

function useResponsiveStyles() {
  const isMobile = window.innerWidth <= 600;
  const isTablet = window.innerWidth > 600 && window.innerWidth <= 900;
  return {
    container: {
      maxWidth: isMobile ? "98vw" : isTablet ? 420 : 340,
      margin: isMobile ? "24px auto" : "60px auto",
      padding: isMobile ? 16 : 32,
      borderRadius: 18,
      boxShadow: "var(--ae-shadow-soft)",
      background: "var(--ae-surface)",
      border: "1px solid var(--ae-border)",
      minHeight: isMobile ? "100vh" : undefined,
      position: "relative",
      overflow: "hidden",
      animation: "fadeInMinimal 0.7s cubic-bezier(.4,0,.2,1)",
      zIndex: 2,
      transition: "box-shadow 0.2s",
    },
    widgets: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? 16 : 32,
      flexWrap: "wrap",
      marginBottom: 32,
    },
    widget: {
      flex: 1,
      minWidth: isMobile ? "90vw" : 220,
      background: "linear-gradient(135deg, var(--ae-accent) 0%, #f8bc85 100%)",
      borderRadius: 10,
      padding: isMobile ? 16 : 24,
      textDecoration: "none",
      color: "var(--ae-primary-strong)",
      boxShadow: "var(--ae-shadow-card)",
      border: "1px solid #ffd7b0",
      fontWeight: 600,
      marginBottom: isMobile ? 8 : 0,
      transition: "background 0.2s, color 0.2s, transform 0.1s",
      display: "block",
    },
    widgetLabel: {
      fontWeight: 700,
      fontSize: 18,
      marginBottom: 6,
    },
    widgetDesc: {
      fontSize: 13,
      color: "#3f4e70",
    },
    header: {
      color: "var(--ae-primary-strong)",
      marginBottom: 8,
      fontSize: isMobile ? 20 : 24,
      textAlign: "center",
      fontWeight: 700,
      letterSpacing: 0.5,
    },
    desc: {
      color: "var(--ae-text-muted)",
      marginBottom: 18,
      fontSize: isMobile ? 14 : 16,
      textAlign: "center",
      fontWeight: 400,
    },
    footer: {
      marginTop: 28,
      textAlign: "center",
      color: "var(--ae-primary)",
      fontSize: isMobile ? 12 : 14,
      fontWeight: 400,
      letterSpacing: 0.3,
    },
  };
}

function Dashboard() {
  const { user } = useAuth();
  const styles = useResponsiveStyles();
  if (!user) return <p>Please log in</p>;

  const rolePriority = ["developer", "superadmin", "admin", "school_admin", "district_admin", "teacher", "special_services_teacher", "parent", "family", "student"];
  const resolvedRoleFromArray = Array.isArray(user.roles)
    ? rolePriority.find((r) => user.roles.includes(r)) || user.roles[0]
    : null;

  const isDeveloper = user.role === "developer" || (Array.isArray(user.roles) && user.roles.includes("developer"));
  if (isDeveloper) return <Navigate to="/developer/home" replace />;

  const isSuperadmin = user.role === "superadmin" || (Array.isArray(user.roles) && user.roles.includes("superadmin"));
  if (isSuperadmin) return <Navigate to="/superadmin/home" replace />;

  // Redirect other roles directly to their home — Dashboard is a fallback only
  const resolvedRole = user.role || resolvedRoleFromArray;
  if (resolvedRole === 'admin' || resolvedRole === 'school_admin' || resolvedRole === 'district_admin') return <Navigate to="/admin" replace />;
  if (resolvedRole === 'teacher' || resolvedRole === 'special_services_teacher') return <Navigate to="/teacher/home" replace />;
  if (resolvedRole === 'student') return <Navigate to="/student/home" replace />;
  if (resolvedRole === 'parent' || resolvedRole === 'family') return <Navigate to="/parent/home" replace />;

  const displayRole = user.role || resolvedRoleFromArray || "user";
  const widgets = roleWidgets[displayRole] || [];

  const handleWidgetMouseOver = (e) => {
    e.currentTarget.style.background = "linear-gradient(135deg, var(--ae-primary) 0%, #2f66c9 100%)";
    e.currentTarget.style.color = "#ffe6cc";
    e.currentTarget.style.transform = "scale(1.03)";
  };

  const handleWidgetMouseOut = (e) => {
    e.currentTarget.style.background = "linear-gradient(135deg, var(--ae-accent) 0%, #f8bc85 100%)";
    e.currentTarget.style.color = "var(--ae-primary-strong)";
    e.currentTarget.style.transform = "scale(1)";
  };

  const renderWidget = (w, i) => {
    if ((displayRole === "parent" || displayRole === "family") && w.action) return null;
    if (displayRole === "teacher" && w.label.toLowerCase().includes("approve")) return null;
    if (displayRole === "student" && w.action && w.label !== "Give Feedback") return null;

    return (
      <Link
        key={i}
        to={w.to}
        style={styles.widget}
        onMouseOver={handleWidgetMouseOver}
        onMouseOut={handleWidgetMouseOut}
      >
        <div style={styles.widgetLabel}>{w.label}</div>
        <div style={styles.widgetDesc}>{w.desc}</div>
      </Link>
    );
  };

  return (
    <>
      <style>{`
        @keyframes fadeInMinimal {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div style={styles.container}>
        <img src="/academic-eye-logo.png" alt="Academic Eye Logo" style={{ display: "block", margin: "0 auto 16px", width: 120, height: 120, borderRadius: 14, boxShadow: "0 2px 8px #3256a2", border: "2px solid #ffa962" }} />
        <div style={styles.header}>{displayRole.charAt(0).toUpperCase() + displayRole.slice(1)} Dashboard</div>
        <div style={styles.desc}>Welcome to Academic Eye — Clear, Standards-Aligned Learning for PK-12.</div>
        <div style={styles.widgets}>
          {widgets.map(renderWidget)}
          {widgets.length === 0 && (
            <div style={{ width: "100%", textAlign: "center", color: "#6b7280" }}>
              No dashboard actions configured for this role.
            </div>
          )}
        </div>
        <div style={styles.footer}>
          <span style={{ color: "var(--ae-accent)" }}>Academic Eye</span> &mdash; Modern PK-12 grading, progress tracking, and communication.
        </div>
      </div>
    </>
  );
}

export default Dashboard;
